import { useEffect, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Divider } from "@mui/material";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiInput from "components/VuiInput";
import VuiSwitch from "components/VuiSwitch";
import VuiAlert from "components/VuiAlert";
import OverlayLoader from "components/OverlayLoader";
import { PERMISSION_SCREENS } from "variables";
import { Formik } from "formik";
import { CreateRoleSchema } from "validations"; // ✅ reuse same schema
import useCommon from "hooks/useCommon";
import { SYSTEM_ROLES } from "variables";

const EditRoleDialog = ({ open, role, onClose, onSave }) => {
  const { STORE } = useCommon();
  const { updateLoading, updateError } = STORE((state) => state.roles);

  const permissionKey = (action, screen) => {
    if (typeof action === "object") {
      return action.key;
    }

    return `${action}${screen}`;
  };

  const calculateRoleLevel = (permissions) => {
    const enabledCount = Object.values(permissions || {}).filter(Boolean).length;

    const totalPossible = PERMISSION_SCREENS.reduce(
      (total, screen) => total + screen.actions.length,
      0
    );

    if (totalPossible === 0) return 1;

    const percentage = Math.round((enabledCount / totalPossible) * 100);

    return Math.min(100, Math.max(1, percentage));
  };

  const dialogPaperRef = useRef(null);

  useEffect(() => {
    if (updateLoading) {
      if (dialogPaperRef.current) dialogPaperRef.current.scrollTop = 0;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [updateLoading]);

  if (!role) return null;

  const isHouseKeeperRole = role.title === SYSTEM_ROLES.HOUSEKEEPER.key;

  const buildScreensEnabled = (permissions) => {
    const enabled = {};

    PERMISSION_SCREENS.forEach((screen) => {
      enabled[screen.key] = screen.actions.some((action) => {
        const key = permissionKey(action, screen.key);
        return !!permissions?.[key];
      });
    });

    return enabled;
  };

  const initialScreensEnabled = buildScreensEnabled(role.permissions);

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
      }}
      maxWidth="md"
      fullWidth
      PaperProps={{
        ref: dialogPaperRef,
        sx: { overflow: updateLoading ? "hidden" : "auto" },
      }}
    >
      <Formik
        enableReinitialize
        initialValues={{
          _id: role._id,
          hotelId: role.hotelId,
          power: role.power,
          title: role.title || "",
          permissions: role.permissions || {},
          screensEnabled: initialScreensEnabled,
        }}
        validationSchema={CreateRoleSchema}
        onSubmit={(values) => {
          const level = calculateRoleLevel(values.permissions);

          const payload = {
            _id: values._id,
            hotelId: values.hotelId,
            power: values.power,
            title: values.title,
            permissions: values.permissions,
            level,
          };

          onSave?.(role._id, payload);
        }}
      >
        {({ values, errors, touched, handleChange, handleSubmit, setFieldValue, resetForm }) => {
          const isHouseKeepingPermissionLocked = (screenKey) =>
            isHouseKeeperRole && screenKey === "HouseKeeping";

          const toggleScreen = (screen, enabled) => {
            if (isHouseKeepingPermissionLocked(screen)) return;

            setFieldValue(`screensEnabled.${screen}`, enabled);

            const screenConfig = PERMISSION_SCREENS.find((s) => s.key === screen);

            if (!enabled) {
              const updated = { ...values.permissions };

              screenConfig.actions.forEach((action) => {
                delete updated[permissionKey(action, screen)];
              });

              setFieldValue("permissions", updated);
            } else {
              const newPermissions = { ...values.permissions };

              screenConfig.actions.forEach((action) => {
                const key = permissionKey(action, screen);
                const actionName = typeof action === "object" ? action.key : action;

                newPermissions[key] = values.permissions?.[key] ?? actionName === "view";
              });

              setFieldValue("permissions", newPermissions);
            }
          };

          const togglePermission = (screen, key, value) => {
            if (isHouseKeepingPermissionLocked(screen)) return;

            setFieldValue(`permissions.${key}`, value);
          };

          const handleCancel = () => {
            resetForm();
            onClose();
          };

          return (
            <form onSubmit={handleSubmit}>
              <DialogTitle>
                <VuiTypography variant="h5">Edit Role</VuiTypography>
              </DialogTitle>

              <DialogContent>
                {updateError && (
                  <VuiAlert color="error" dismissible size="sm">
                    {updateError.message || "Failed to update role"}
                  </VuiAlert>
                )}

                <VuiBox mb={2}>
                  <VuiTypography variant="caption" ml={1}>
                    Role Title
                  </VuiTypography>

                  <VuiInput
                    gradient
                    fullWidth
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    disabled={isHouseKeeperRole}
                    error={touched.title && !!errors.title}
                  />

                  {isHouseKeeperRole && (
                    <VuiTypography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 0.5,
                        ml: 1,
                        opacity: 0.6,
                        fontSize: "11px",
                      }}
                    >
                      This is a system role and its title cannot be changed.
                    </VuiTypography>
                  )}

                  {touched.title && errors.title && (
                    <VuiTypography variant="caption" color="error">
                      {errors.title}
                    </VuiTypography>
                  )}
                </VuiBox>

                <Divider />

                <VuiBox mt={2}>
                  <VuiTypography variant="button" mb={2}>
                    Permissions
                  </VuiTypography>

                  {PERMISSION_SCREENS.map(({ key, label, actions }) => (
                    <VuiBox key={key} mb={2}>
                      <VuiBox display="flex" justifyContent="space-between" alignItems="center">
                        <VuiTypography variant="body2">{label}</VuiTypography>

                        <VuiSwitch
                          color="success"
                          checked={!!values.screensEnabled?.[key]}
                          disabled={isHouseKeepingPermissionLocked(key)}
                          onChange={(e) => toggleScreen(key, e.target.checked)}
                        />
                      </VuiBox>

                      {values.screensEnabled?.[key] && (
                        <VuiBox display="flex" gap={3} mt={1} pl={2}>
                          {actions.map((action) => {
                            const permKey = permissionKey(action, key);

                            return (
                              <VuiBox key={permKey} display="flex" alignItems="center" gap={1}>
                                <VuiSwitch
                                  color="success"
                                  checked={!!values.permissions?.[permKey]}
                                  disabled={isHouseKeepingPermissionLocked(key)}
                                  onChange={(e) => togglePermission(key, permKey, e.target.checked)}
                                />
                                <VuiTypography variant="caption">
                                  {typeof action === "object"
                                    ? action.label
                                    : action.charAt(0).toUpperCase() + action.slice(1)}
                                </VuiTypography>
                              </VuiBox>
                            );
                          })}
                        </VuiBox>
                      )}
                    </VuiBox>
                  ))}

                  {touched.permissions && errors.permissions && (
                    <VuiTypography variant="caption" color="error">
                      {errors.permissions}
                    </VuiTypography>
                  )}
                </VuiBox>
              </DialogContent>

              <DialogActions>
                <VuiButton variant="outlined" color="primary" onClick={handleCancel}>
                  Cancel
                </VuiButton>
                <VuiButton
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={updateLoading}
                >
                  {updateLoading ? "Saving..." : "Save Changes"}
                </VuiButton>
              </DialogActions>
            </form>
          );
        }}
      </Formik>

      <OverlayLoader open={updateLoading} message="Saving role changes..." />
    </Dialog>
  );
};

export default EditRoleDialog;
